import { IExcelReportData, ITableData } from 'types/excelExport';
import {
    IProFormaReturnsData,
    IDispositionData,
    IOperatingPeriodCashFlows,
    IOperatingAndPermanentDebtInfo,
} from 'views/ProFormaTable/types';
import {
    createHeaderRow,
    createDataRow,
    createBlankRow,
    createMetricRow,
    parseNumber,
} from './helpers';
import { EXCEL_COLORS, EXCEL_NUM_FORMATS } from './constants';
import { getModelExportConfig } from './modelConfig';

interface IReturnsSummarySheetParams {
    dealName: string;
    returns: IProFormaReturnsData;
    disposition: IDispositionData | undefined;
    operatingInfo?: IOperatingAndPermanentDebtInfo;
    proFormaModelId: number;
    cashFlows: (IOperatingPeriodCashFlows | null)[];
    stabilizedMonth?: number | null;
    showCents?: boolean;
}

const createSectionHeader = (label: string, colCount: number = 3): ITableData => ({
    rowCells: [
        { value: label, cellFont: { bold: true }, width: 30 },
        ...Array(colCount - 1).fill({ value: '' }),
    ],
    rowFill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: EXCEL_COLORS.DARK_GRAY },
    },
});

const calculateStabilizedNoi = (
    cashFlows: (IOperatingPeriodCashFlows | null)[],
    stabilizedMonth?: number | null,
): number | null => {
    if (!stabilizedMonth || !cashFlows.length) return null;

    const startIndex = stabilizedMonth - 1; // Convert to 0-based index
    const endIndex = Math.min(startIndex + 12, cashFlows.length);

    let total = 0;
    for (let i = startIndex; i < endIndex; i++) {
        const cf = cashFlows[i];
        if (cf?.netOperatingIncome) {
            total += parseNumber(cf.netOperatingIncome) || 0;
        }
    }

    return total;
};

export const formatReturnsSummarySheet = ({
    dealName,
    returns,
    disposition,
    operatingInfo,
    proFormaModelId,
    cashFlows,
    stabilizedMonth,
    showCents = false,
}: IReturnsSummarySheetParams): IExcelReportData => {
    const config = getModelExportConfig(proFormaModelId);
    const { returnsTab } = config;

    const today = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

    const topHeaderRows: ITableData[] = [
        createDataRow([{ value: dealName, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: 'Returns Summary', cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: today, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createBlankRow(3),
    ];

    const propertyReturnsHeader = createSectionHeader(returnsTab.propertyReturns.sectionHeader, 3);
    const propertyReturnsColumnHeader = createHeaderRow(['Metric', 'Unlevered', 'Levered'], EXCEL_COLORS.BLUE_HEADER, true);

    const propertyReturnsRows: ITableData[] = [
        createMetricRow(returnsTab.propertyReturns.irr, [
            returns?.netUnleveredIrr,
            returns?.netLeveredIrr,
        ], EXCEL_NUM_FORMATS.PERCENT),
        createMetricRow(returnsTab.propertyReturns.equityMultiple, [
            parseNumber(returns?.netUnleveredEquityMultiple),
            returns?.netLeveredEquityMultiple,
        ], EXCEL_NUM_FORMATS.MULTIPLE),
        createMetricRow(returnsTab.propertyReturns.roi, [
            returns?.unleveredRoi,
            returns?.leveredRoi,
        ], EXCEL_NUM_FORMATS.PERCENT),
    ];

    const devReturnsHeader = createSectionHeader(returnsTab.developmentReturns.sectionHeader, 4);
    const devReturnsColumnHeader = createHeaderRow(['Metric', 'Untrended', 'Trended', 'At Sale'], EXCEL_COLORS.BLUE_HEADER, true);

    const marketCapRateCurrent = disposition?.marketCapitalizationRate?.current ?? returns?.marketCapitalizationRate?.current;
    const marketCapRateTrended = disposition?.marketCapitalizationRate?.trended ?? returns?.marketCapitalizationRate?.trended;
    const marketCapRateSale = disposition?.marketCapitalizationRate?.onSale ?? returns?.marketCapitalizationRate?.onSale;

    const devReturnsRows: ITableData[] = [
        createMetricRow(returnsTab.developmentReturns.yieldOnCost, [
            returns?.yieldOnCost?.untrended,
            returns?.yieldOnCost?.trended,
            returns?.yieldOnCost?.sale,
        ], EXCEL_NUM_FORMATS.PERCENT),
        createMetricRow(returnsTab.developmentReturns.marketCapRate, [
            marketCapRateCurrent,
            marketCapRateTrended,
            marketCapRateSale,
        ], EXCEL_NUM_FORMATS.PERCENT),
        createMetricRow(returnsTab.developmentReturns.developmentSpread, [
            returns?.developmentSpread?.untrended != null ? returns.developmentSpread.untrended / 10000 : null,
            returns?.developmentSpread?.trended != null ? returns.developmentSpread.trended / 10000 : null,
            returns?.developmentSpread?.sale != null ? returns.developmentSpread.sale / 10000 : null,
        ], EXCEL_NUM_FORMATS.PERCENT),
    ];

    const keyStatsHeader = createSectionHeader(returnsTab.keyStatistics.sectionHeader, 2);

    const egr = disposition?.totalEffectiveGrossRevenue?.trended || 0;
    const opex = disposition?.totalOperatingExpenses?.trended || 0;
    const operatingExpenseRatio = egr > 0 ? Math.abs(opex) / egr : null;

    const stabilizedNoi = calculateStabilizedNoi(cashFlows, stabilizedMonth);

    const currencyFmt = showCents ? EXCEL_NUM_FORMATS.CURRENCY_CENTS : EXCEL_NUM_FORMATS.CURRENCY;
    const cashOnCashReturn = parseNumber(operatingInfo?.cashOnCashReturn);

    const keyStatsRows: ITableData[] = [
        createDataRow([
            { value: returnsTab.keyStatistics.operatingExpenseRatio, width: 30 },
            {
                value: operatingExpenseRatio ?? 0,
                width: 15,
                alignment: { horizontal: 'right' },
                numFmt: EXCEL_NUM_FORMATS.PERCENT,
            },
        ]),
        createDataRow([
            { value: returnsTab.keyStatistics.cashOnCashReturn, width: 30 },
            {
                value: cashOnCashReturn ?? 0,
                width: 15,
                alignment: { horizontal: 'right' },
                numFmt: EXCEL_NUM_FORMATS.PERCENT,
            },
        ]),
        createDataRow([
            { value: returnsTab.keyStatistics.stabilizedNoi, width: 30 },
            {
                value: stabilizedNoi ?? 0,
                width: 15,
                alignment: { horizontal: 'right' },
                numFmt: currencyFmt,
            },
        ]),
    ];

    return {
        topHeaderRows,
        reportTables: [
            {
                tableHeaderRows: [propertyReturnsHeader, propertyReturnsColumnHeader],
                tableData: propertyReturnsRows,
            },
            {
                tableTitleRows: [createBlankRow(4)],
                tableHeaderRows: [devReturnsHeader, devReturnsColumnHeader],
                tableData: devReturnsRows,
            },
            {
                tableTitleRows: [createBlankRow(2)],
                tableHeaderRows: [keyStatsHeader],
                tableData: keyStatsRows,
            },
        ],
    };
};
